import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output
} from '@angular/core';

@Component({
  selector: 'app-account-pin-pad',
  standalone: false,
  templateUrl: './account-pin-pad.html',
  styleUrl: './account-pin-pad.css'
})
export class AccountPinPad {
  @Input() visible = true;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() modal = false;
  @Input() pin = '';
  @Output() pinChange = new EventEmitter<string>();

  @Input() title = 'Enter account PIN';
  @Input() description = 'Enter your six-digit account PIN to continue.';
  @Input() confirmLabel = 'Continue';
  @Input() loading = false;
  @Input() error = '';
  @Input() showCloseButton = true;

  readonly digits = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  readonly pinSlots = [0, 1, 2, 3, 4, 5];

  get active(): boolean {
    return !this.modal || this.visible;
  }

  get complete(): boolean {
    return /^\d{6}$/.test(this.pin);
  }

  appendDigit(digit: number): void {
    if (this.loading || this.pin.length >= 6) {
      return;
    }

    this.updatePin(`${this.pin}${digit}`);
  }

  removeDigit(): void {
    if (this.loading || !this.pin.length) {
      return;
    }

    this.updatePin(this.pin.slice(0, -1));
  }

  clear(): void {
    if (this.loading || !this.pin.length) {
      return;
    }

    this.updatePin('');
  }

  submit(): void {
    if (this.loading || !this.complete) {
      return;
    }

    this.pinSubmit.emit(this.pin);
  }

  close(): void {
    if (!this.modal || this.loading) {
      return;
    }

    this.updatePin('');
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancelled.emit();
  }

  trackByIndex(index: number): number {
    return index;
  }

  @Output() pinSubmit = new EventEmitter<string>();
  @Output() cancelled = new EventEmitter<void>();

  @HostListener('document:keydown', ['$event'])
  handleKeyboard(event: KeyboardEvent): void {
    if (!this.active || this.loading) {
      return;
    }

    const target = event.target as HTMLElement | null;
    const tagName = target?.tagName?.toLowerCase();
    const isEditable = !!target?.isContentEditable
      || tagName === 'input'
      || tagName === 'textarea'
      || tagName === 'select';

    if (isEditable && !this.modal) {
      return;
    }

    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      this.appendDigit(Number(event.key));
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      this.removeDigit();
      return;
    }

    if (event.key === 'Escape' && this.modal) {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Enter' && this.complete) {
      event.preventDefault();
      this.submit();
    }
  }

  private updatePin(value: string): void {
    this.pin = value.replace(/\D/g, '').slice(0, 6);
    this.pinChange.emit(this.pin);
  }
}
